import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormField } from "../../../../Components/FormField";
import TextInput from "../../../../Components/TextInput";
import { ErrorMessage, Form, Formik } from "formik";
import SelectDropdown from "../../../../Components/Dropdown/SelectDropdown";
import { Checkbox } from "primereact/checkbox";
import { useFetchAll } from "../../../../custom/FetchApiHook";
import { UsersApi } from "../../../../App/api/users.api";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { approvalRoutes } from "../../../../Router/router";
import { multilevelApprovalApi } from "../../../../App/api/multilevelApproval.api";
import { toast } from "react-toastify";
import { RxCross2 } from "react-icons/rx";

const MultiLevelApproval = () => {
  const [approvalMenu, setApprovalMenu] = useState(false);
  const [isApproval, setIsApproval] = useState(true);
  const [editData, setEditData] = useState(null);

  const [pagination] = useState({
    page: 1,
    limit: 10000,
    sortOrder: "desc",
    filter: { status: "Approved" },
  });

  const { value: usersData, fetchAllData: fetchUsers } = useFetchAll(() =>
    UsersApi.getAllUsers(pagination),
  );

  const { value: allApprovals, fetchAllData: getAllApprovals } = useFetchAll(
    () => multilevelApprovalApi.getAllApprovals(),
  );

  const usersOptions = useMemo(() => {
    return (
      usersData?.data?.user?.map((d) => ({
        name: `${d.first_name} ${d.last_name}`,
        user_role: d.user_role,
        _id: d._id,
        ...d, // keep original if needed (ex: first_name)
      })) || []
    );
  }, [usersData]);

  const componentsOptions = useMemo(() => {
    return approvalRoutes?.map((r) => r.meta) || [];
  }, []);

  const generateLevelOptions = (levels) => {
    const options = [];
    for (let i = 1; i <= levels; i++) options.push({ name: `Level-${i}` });
    return options;
  };

  const changeMenu = (v) => setApprovalMenu(v);

  const handleClose = useCallback(() => {
    setEditData(null);
    setApprovalMenu(false);

    // Reset the "approvalData" add section UI reliably
    setIsApproval(false);
    setTimeout(() => setIsApproval(true), 10);
  }, []);

  const openCreate = useCallback(() => {
    // ✅ FIX: Create must always open an EMPTY form
    setEditData(null);
    setApprovalMenu(true);

    // reset add-row UI
    setIsApproval(false);
    setTimeout(() => setIsApproval(true), 10);
  }, []);

  const openEdit = useCallback((row) => {
    setEditData(row);
    setApprovalMenu(true);

    // reset add-row UI
    setIsApproval(false);
    setTimeout(() => setIsApproval(true), 10);
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = { ...values };
      delete payload.approvalData;

      const isEdit = Boolean(editData?._id);

      if (isEdit) {
        await multilevelApprovalApi.updateApproval(payload, editData._id);
      } else {
        await multilevelApprovalApi.createApproval(payload);
      }

      toast.success(isEdit ? "Approval updated!" : "Approval created!");

      await getAllApprovals();
      handleClose();
      resetForm();
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getAllApprovals();
    fetchUsers();
  }, []); // eslint-disable-line

  return (
    <div className="p-4 md:p-8 bg-[--bg-color] h-[95vh] overflow-auto">
      {/* Header */}
      {/* <div className="flex items-center justify-between gap-3 border-b-2  border-[--bg-tbl-header] pb-4"> */}
        <div className="flex items-center justify-between border-b-2 pb-4 gap-5">
          <button
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
            className="border border-[var(--bg-tbl-header)] hover:bg-red-400 rounded-full w-44 h-10 text-color2 text-sm hover:text-white cursor-pointer transition"
          >
            All Approvals
          </button>

          {!approvalMenu && (
            <button
              onClick={(e) => {
                e.preventDefault();
                openCreate();
              }}
              className="border border-[var(--bg-tbl-header)] hover:bg-red-400 rounded-full w-44 h-10 text-color2 text-sm hover:text-white cursor-pointer transition"
            >
              Create Approval
            </button>
          )}
        </div>

       
      {/* </div> */}

      {approvalMenu ? (
        <div className="mt-6">
          {/* Form Card */}
          <div className="relative p-5 md:p-7 rounded-2xl border border-[--bg-tbl-header] bg-[--bg-color] shadow-md">
            {/* Top Row */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-[--dark-color3]">
                  {editData?._id ? "Update Approval" : "Create Approval"}
                </h1>
                <p className="text-sm opacity-70 text-[--dark-color3] mt-1">
                  Configure approval info and define multi-level approval flow.
                </p>
              </div>

              {/* ✅ Close X Button */}
              <button
                type="button"
                onClick={handleClose}
                className="w-10 h-10 rounded-full border border-[--bg-tbl-header] hover:bg-red-400 hover:text-white text-[--dark-color3] flex items-center justify-center transition"
                title="Close"
              >
                <RxCross2 className="text-lg" />
              </button>
            </div>

            <Formik
              initialValues={{
                name: editData?.name || "",
                component: editData?.component || "",
                active: editData?.active || false,

                // temp row holder
                approvalData: {
                  approval: null,
                  level: null,
                  user: null,
                },

                // array
                approvalFlow: editData?.approvalFlow?.length ? editData.approvalFlow : [],
              }}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ setFieldValue, values, resetForm, isSubmitting }) => (
                <Form>
                  {/* Sections Grid */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {/* Approval Info */}
                    <div className="rounded-2xl border border-[--bg-tbl-header] p-4 md:p-5">
                      <h2 className="text-sm font-bold mb-4 text-[--dark-color3]">
                        Approval Info
                      </h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          className="flex-1"
                          label="Approval Name"
                          tooltip="Enter Approval Name"
                        >
                          <TextInput
                            name="name"
                            type="text"
                            value={values.name}
                            placeholder="Enter Approval name"
                            className="outline-none h-[2.6rem]"
                            onChange={(e) => setFieldValue("name", e.target.value)}
                          />
                          <ErrorMessage name="name" component="p" className="error" />
                        </FormField>

                        <FormField
                          className="flex-1"
                          label="Component"
                          tooltip="Choose Component"
                        >
                          <SelectDropdown
                            sx={{ borderRadius: "3px", height: "2.6rem" }}
                            value={values.component}
                            inputName="component"
                            placeholder="Select Component"
                            options={componentsOptions}
                            handleFilter={(name, v) => setFieldValue("component", v)}
                            optionLabel="entityType"
                            optionValue="entityType"
                          />
                          <ErrorMessage name="component" component="p" className="error" />
                        </FormField>

                        <div className="flex items-center gap-3 mt-1">
                          <Checkbox
                            id="approval_active"
                            name="active"
                            value={values.active}
                            checked={values.active}
                            onClick={() => setFieldValue("active", !values.active)}
                            className="cursor-pointer"
                          />
                          <label
                            htmlFor="approval_active"
                            className="text-sm font-medium cursor-pointer text-[--dark-color3]"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Approval Definition Add Row */}
                    <div className="rounded-2xl border border-[--bg-tbl-header] p-4 md:p-5">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h2 className="text-sm font-bold text-[--dark-color3]">
                          Approval Definition
                        </h2>

                        <span className="text-xs px-3 py-1 rounded-full border border-[--bg-tbl-header] text-[--dark-color3] opacity-80">
                          Add / Edit flow rows
                        </span>
                      </div>

                      {isApproval ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                          <FormField label="Approval" tooltip="Select Approval">
                            <SelectDropdown
                              sx={{ borderRadius: "3px", height: "2.6rem" }}
                              value={values.approvalData?.approval}
                              inputName="approval"
                              placeholder="Select Approval"
                              options={[{ name: "Mandatory" }, { name: "Non-Mandatory" }]}
                              handleFilter={(name, v) => {
                                const copy = { ...values.approvalData, approval: v };
                                setFieldValue("approvalData", copy);
                              }}
                              optionLabel="name"
                              optionValue="name"
                            />
                          </FormField>

                          <FormField label="Level" tooltip="Select Level">
                            <SelectDropdown
                              sx={{ borderRadius: "3px", height: "2.6rem" }}
                              value={values.approvalData?.level}
                              inputName="level"
                              placeholder="Select Level"
                              options={generateLevelOptions(20)}
                              handleFilter={(name, v) => {
                                const copy = { ...values.approvalData, level: v };
                                setFieldValue("approvalData", copy);
                              }}
                              optionLabel="name"
                              optionValue="name"
                            />
                          </FormField>

                          <FormField label="Users" tooltip="Select Users">
                            <SelectDropdown
                              sx={{ borderRadius: "3px", height: "2.6rem" }}
                              value={values.approvalData?.user}
                              inputName="user"
                              placeholder="Select Users"
                              options={usersOptions}
                              handleFilter={(name, v) => {
                                const selectedUserObj =
                                  usersOptions?.find((u) => u._id === v) || null;

                                const copy = {
                                  ...values.approvalData,
                                  user: selectedUserObj || v,
                                };
                                setFieldValue("approvalData", copy);
                              }}
                              optionLabel="name"
                              optionValue="_id"
                            />
                          </FormField>

                          <div className="md:col-span-3 flex justify-end">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();

                                const approvalFlowCopy = [...values.approvalFlow];

                                if (values.approvalData?.index !== undefined) {
                                  approvalFlowCopy[Number(values.approvalData.index)] =
                                    values.approvalData;
                                } else {
                                  approvalFlowCopy.push(values.approvalData);
                                }

                                setFieldValue("approvalFlow", approvalFlowCopy);

                                // reset add row fields
                                setIsApproval(false);
                                setTimeout(() => {
                                  setFieldValue("approvalData", {
                                    approval: null,
                                    level: null,
                                    user: null,
                                  });
                                  setIsApproval(true);
                                }, 10);
                              }}
                              className="btn w-[7.5rem]"
                            >
                              {values.approvalData?.index !== undefined ? "Update Row" : "Add Row"}
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {/* Flow Table */}
                      <div className="mt-6 rounded-2xl border border-[--bg-tbl-header] overflow-hidden">
                        <div className="px-4 py-3 bg-[var(--bg-tbl-header)]">
                          <h3 className="text-sm font-bold text-[--dark-color3]">
                            Users added for Approval
                          </h3>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-[var(--bg-tbl-header)] border-[--bg-color]">
                                <th className="border border-gray-200 px-4 py-2">S.No</th>
                                <th className="border border-gray-200 px-4 py-2">Approvals</th>
                                <th className="border border-gray-200 px-4 py-2">Levels</th>
                                <th className="border border-gray-200 px-4 py-2">Users</th>
                                <th className="border border-gray-200 px-4 py-2 sticky right-0 bg-[var(--bg-tbl-header)]">
                                  Actions
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {values.approvalFlow?.length ? (
                                values.approvalFlow.map((d, i) => (
                                  <tr key={i} className="text-[--dark-color3]">
                                    <td className="border border-gray-200 px-4 py-2 text-center">
                                      {i + 1}
                                    </td>
                                    <td className="border border-gray-200 px-4 py-2 text-center">
                                      {d.approval}
                                    </td>
                                    <td className="border border-gray-200 px-4 py-2 text-center">
                                      {d.level}
                                    </td>
                                    <td className="border border-gray-200 px-4 py-2 text-center">
                                      {/* supports either stored user object or fallback */}
                                      {d.user?.first_name
                                        ? `${d.user.first_name} ${d.user?.last_name || ""}`
                                        : d.user?.name || "-"}
                                    </td>

                                    <td className="border border-gray-200 px-4 py-2 sticky right-0 bg-[--bg-color]">
                                      <div className="flex justify-center items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            const copy = [...values.approvalFlow];
                                            copy.splice(i, 1);
                                            setFieldValue("approvalFlow", copy);
                                          }}
                                          className="btn-primary w-[2.5rem] h-[2.5rem] rounded-full flex justify-center items-center cursor-pointer"
                                          title="Delete"
                                        >
                                          <MdDelete className="text-[1.3rem]" />
                                        </button>

                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setFieldValue("approvalData", {
                                              index: `${i}`,
                                              ...d,
                                            });

                                            setIsApproval(false);
                                            setTimeout(() => setIsApproval(true), 10);
                                          }}
                                          className="btn-primary w-[2.5rem] h-[2.5rem] rounded-full flex justify-center items-center cursor-pointer"
                                          title="Edit row"
                                        >
                                          <FaEdit className="text-[1.3rem]" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="11" className="text-center text-gray-500 py-6">
                                    No Rows To Show
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Actions (Clear + Submit) */}
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        // clear form completely and keep create mode empty
                        resetForm();
                        setEditData(null);

                        // reset row add UI
                        setIsApproval(false);
                        setTimeout(() => setIsApproval(true), 10);
                      }}
                      className="btn-primary w-[7rem]"
                      disabled={isSubmitting}
                    >
                      Clear
                    </button>

                    <button
                      type="submit"
                      className="btn w-[7rem]"
                      disabled={isSubmitting}
                    >
                      {editData?._id ? "Update" : "Submit"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-[--bg-tbl-header] bg-[--bg-color] shadow-md overflow-hidden">
          <div className="px-4 md:px-6 py-4 bg-[var(--bg-tbl-header)] flex items-center justify-between">
            <h2 className="text-lg font-bold text-[--dark-color3]">Approval Flow Table</h2>

            <button
              onClick={(e) => {
                e.preventDefault();
                openCreate();
              }}
              className="border border-[var(--bg-tbl-header)] hover:bg-red-400 rounded-full w-44 h-10 text-color2 text-sm hover:text-white cursor-pointer transition"
            >
              Create Approval
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse border border-gray-200">
              <thead className="bg-[var(--bg-tbl-header)]">
                <tr className="border-[--bg-color]">
                  <th className="border border-gray-200 px-4 py-2">S.No</th>
                  <th className="border border-gray-200 px-4 py-2">Approval Name</th>
                  <th className="border border-gray-200 px-4 py-2">Component</th>
                  <th className="border border-gray-200 px-4 py-2">Status</th>
                  <th className="border border-gray-200 px-4 py-2 sticky right-0 bg-[var(--bg-tbl-header)]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {allApprovals?.data?.length ? (
                  allApprovals.data.map((d, i) => (
                    <tr
                      key={i}
                      className="text-[--dark-color3] border border-gray-200"
                    >
                      <td className="text-center border border-gray-200 py-4">
                        {i + 1}
                      </td>
                      <td className="text-center border border-gray-200 py-4">
                        {d?.name}
                      </td>
                      <td className="text-center border border-gray-200 py-4">
                        {d?.component}
                      </td>
                      <td className="text-center border border-gray-200 py-4">
                        {d?.active ? "Active" : "Inactive"}
                      </td>

                      <td className="border border-gray-200 px-4 py-2 sticky right-0 bg-[--bg-color]">
                        <div className="flex justify-center items-center gap-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              // delete logic can be added here
                            }}
                            className="btn-primary w-[2.5rem] h-[2.5rem] rounded-full flex justify-center items-center cursor-pointer"
                            title="Delete"
                          >
                            <MdDelete className="text-[1.3rem]" />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              openEdit(d);
                            }}
                            className="btn-primary w-[2.5rem] h-[2.5rem] rounded-full flex justify-center items-center cursor-pointer"
                            title="Edit"
                          >
                            <FaEdit className="text-[1.3rem]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center text-gray-500 py-6">
                      No Rows To Show
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLevelApproval;
